import type {
  FormationTvdGrid,
  GunBarrelSlotItem,
  GunBarrelSlotLocation,
  GunBarrelStructureFormation,
  GunBarrelTypeCurveFormation,
  GunBarrelWellPoint,
} from './types';

/**
 * The chart plots in TVD. The payload mostly speaks TVDSS, and converting between them
 * needs the surface (kelly-bushing) elevation: `TVD = ELEV − TVDSS`.
 *
 * Nothing in the response states that elevation outright, so it is inferred: any pair of
 * TVD and TVDSS readings taken at the same point sums to it. The backend builds every
 * formation's TVD array from one TVDSS array and one `surface_ft`, so `topTvd + topTvdss`
 * is that elevation exactly, at every sample.
 */

/** A formation of either kind — both carry the same four depth grids. */
export type DepthGridSource = Pick<
  GunBarrelStructureFormation | GunBarrelTypeCurveFormation,
  'topTvd' | 'topTvdss' | 'baseTvd' | 'baseTvdss'
>;

/**
 * Surface elevation from the first cell where a grid holds BOTH depths for the same point.
 * @param {FormationTvdGrid | null} tvdGrid The TVD grid, null when the backend omitted it.
 * @param {FormationTvdGrid} tvdssGrid The matching TVDSS grid.
 * @return {number | null} The elevation, or null when the pair is unavailable.
 */
const elevationFromGrids = (
  tvdGrid: FormationTvdGrid | null,
  tvdssGrid: FormationTvdGrid
): number | null => {
  if (!tvdGrid) return null;

  for (let row = 0; row < tvdGrid.length; row += 1) {
    const tvdRow = tvdGrid[row] ?? [];
    const tvdssRow = tvdssGrid[row] ?? [];
    for (let col = 0; col < tvdRow.length; col += 1) {
      const tvd = tvdRow[col];
      const tvdss = tvdssRow[col];
      if (Number.isFinite(tvd) && Number.isFinite(tvdss)) {
        return (tvd as number) + (tvdss as number);
      }
    }
  }

  return null;
};

export interface DepthBasisSources {
  formations?: DepthGridSource[];
  typeCurveFormations?: DepthGridSource[];
  slots?: Pick<GunBarrelSlotItem, 'tvdFt' | 'tvdssFt'>[];
  wells?: Pick<GunBarrelWellPoint, 'tvdFt' | 'tvdssFt'>[];
}

/**
 * The surface elevation this dataset should be drawn against.
 *
 * Ordered by reliability, not convenience. The formation grids come first because they are
 * the datum's own source — the backend derives their TVD from it — and because they are
 * present even for a unit with no wells at all, which is the case this resolver exists for:
 * a pure-LTD unit sends `wells: []`, and reading the elevation only from a well left it at
 * 0, collapsing every `ELEV − TVDSS` to a plain negation.
 *
 * Wells come next, for the case the grids genuinely cannot answer: the backend emits
 * `topTvd: null` when it cannot resolve `surface_ft`, and a well then carries the only
 * TVD/TVDSS pair in the payload. A well's pair is exact — the backend derives its TVD as
 * `surface_ft − tvdss` from the well's own kelly bushing — so it is a true elevation, just
 * that well's rather than the section's.
 *
 * Slots are last, and answer for exactly one case: a WELL-backed slot in a dataset given
 * `slots` but no `wells` (they are separate props, and the chart is mounted both ways). The
 * backend reads such a slot's TVD off the well's own survey hit, so it still carries a
 * TVD/TVDSS pair when `surface_ft` is unresolvable and the grids are therefore TVD-less. An
 * LTD slot cannot answer here — no `surface_ft` means no slot TVD either — so this tier is
 * only ever a neighbouring well's kelly bushing, the same quality as the tier above it.
 * Keeping it is what stops that dataset collapsing to the 0 below.
 *
 * Returns 0 when nothing can answer, which leaves the chart plotting in TVDSS magnitude —
 * wrong, but it is what the caller has always fallen back to and there is no better guess.
 * @param {DepthBasisSources} sources Whatever the current dataset provides.
 * @return {number} The surface elevation in feet.
 */
export const resolveSurfaceElevation = ({
  formations = [],
  typeCurveFormations = [],
  slots = [],
  wells = [],
}: DepthBasisSources): number => {
  for (const formation of [...formations, ...typeCurveFormations]) {
    const fromTop = elevationFromGrids(formation.topTvd, formation.topTvdss);
    if (fromTop !== null) return fromTop;

    const fromBase = elevationFromGrids(formation.baseTvd, formation.baseTvdss);
    if (fromBase !== null) return fromBase;
  }

  const well = wells.find(
    (w) => Number.isFinite(w.tvdFt) && Number.isFinite(w.tvdssFt)
  );
  if (well) return well.tvdFt + well.tvdssFt;

  const slot = slots.find(
    (s) => Number.isFinite(s.tvdFt) && Number.isFinite(s.tvdssFt)
  );
  if (slot) return (slot.tvdFt as number) + slot.tvdssFt;

  return 0;
};

/**
 * The TVD to plot a THEORETICAL slot location at (`slotLocations` on a type-curve formation).
 *
 * Prefers the TVD the backend sent, exactly as the formation bands prefer their explicit
 * `topTvd` grid over deriving one — and for the same reason: both values come out of a single
 * `formation_mid_depth_at` sample, so the server's is the unrounded truth and deriving one
 * only re-does its arithmetic.
 *
 * NOT for the real slots in the `insights_unit_formation_slots` layer. Those plot off `tvdssFt`
 * alone, deliberately — see the comment at their build site in `gun-barrel-chart.tsx`.
 * @param {object} slot The slot location's two depth readings.
 * @param {number} surfaceElevation Elevation for the fallback conversion.
 * @return {number} The TVD in feet.
 */
export const resolveSlotTvd = (
  slot: Pick<GunBarrelSlotLocation, 'tvdFt' | 'tvdssFt'>,
  surfaceElevation: number
): number => {
  if (Number.isFinite(slot.tvdFt)) return slot.tvdFt as number;

  return surfaceElevation - slot.tvdssFt;
};
